import React, { useState, useEffect, useCallback } from 'react';
import { Mail, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import EmailStatTiles from './email-activity/EmailStatTiles';
import EmailActivityFilters from './email-activity/EmailActivityFilters';
import EmailActivityTable from './email-activity/EmailActivityTable';
import EmailDetailDialog from './email-activity/EmailDetailDialog';
import { PAGE_SIZE } from './email-activity/utils';

const EmailActivityPanel = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);

  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);

  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [days, setDays] = useState(30);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [hideTest, setHideTest] = useState(true);

  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(true);
  const [detailRow, setDetailRow] = useState(null);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Reset paging when filters change
  useEffect(() => {
    setSkip(0);
  }, [statusFilter, categoryFilter, days, searchDebounced, hideTest]);

  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const r = await api.get('/admin/email-logs/stats', { params: { days, hide_test: hideTest } });
      setStats(r.data);
    } catch (e) {
      toast.error('Failed to load email stats');
    } finally {
      setStatsLoading(false);
    }
  }, [days, hideTest]);

  const fetchCategories = useCallback(async () => {
    try {
      const r = await api.get('/admin/email-logs/categories', { params: { hide_test: hideTest } });
      setCategories(r.data?.categories || []);
    } catch {/* non-fatal */ }
  }, [hideTest]);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get('/admin/email-logs', {
        params: {
          status: statusFilter,
          category: categoryFilter,
          q: searchDebounced,
          days,
          limit: PAGE_SIZE,
          skip,
          hide_test: hideTest,
        },
      });
      setLogs(r.data?.logs || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error('Failed to load email logs');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter, searchDebounced, days, skip, hideTest]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // If user toggles hideTest and their active category is no longer in the
  // list (because it was a _test_* category), reset it to "all".
  useEffect(() => {
    if (categoryFilter !== 'all' && categories.length > 0 && !categories.includes(categoryFilter)) {
      setCategoryFilter('all');
    }
  }, [categories, categoryFilter]);

  const handleRefresh = () => {
    fetchStats();
    fetchLogs();
  };

  return (
    <div className="space-y-6" data-testid="email-activity-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Mail className="h-6 w-6 text-red-400" />
            Email Activity
          </h1>
          <p className="text-sm text-slate-400">
            Read-only view of every email sent through SendGrid. Diagnose
            delivery issues, verify sender configuration, audit notifications.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleRefresh}
          disabled={loading || statsLoading}
          className="border-[#2A3658] bg-transparent text-slate-200 hover:bg-[#131C3A]"
          data-testid="email-activity-refresh"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${(loading || statsLoading) ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <EmailStatTiles stats={stats} statsLoading={statsLoading} days={days} />

      <EmailActivityFilters
        search={search}
        setSearch={setSearch}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        days={days}
        setDays={setDays}
        hideTest={hideTest}
        setHideTest={setHideTest}
        categories={categories}
        categoryFilter={categoryFilter}
        setCategoryFilter={setCategoryFilter}
      />

      <EmailActivityTable
        logs={logs}
        total={total}
        loading={loading}
        skip={skip}
        setSkip={setSkip}
        onRowClick={setDetailRow}
      />

      <EmailDetailDialog detailRow={detailRow} onClose={() => setDetailRow(null)} />
    </div>
  );
};

export default EmailActivityPanel;
